import { useMemo } from 'react';
import { Route, Routes } from 'react-router';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { ConditionFormFromGetMany, useConditionForms } from '@/models/conditionForm';

import { ConditionForm as ConditionFormPage } from './ConditionForm/ConditionForm';

const CONDITION_FORM_TYPE_ORDER: Record<ConditionFormFromGetMany['type'], number> = {
  initial_condition: 0,
  quality_control: 1,
};

export const ConditionFormsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<ConditionForms />} />
      <Route path="/:id/*" element={<ConditionFormPage />} />
    </Routes>
  );
};

const ConditionForms = () => {
  const { t } = useLingui();

  const {
    data: { conditionForms = [] } = {},
    isLoading: isConditionFormsLoading,
    isError: isConditionFormsError,
  } = useConditionForms();

  const sortedConditionForms = useMemo(
    () =>
      [...conditionForms].sort((a, b) => {
        const typeOrder = CONDITION_FORM_TYPE_ORDER[a.type] - CONDITION_FORM_TYPE_ORDER[b.type];

        if (typeOrder !== 0) {
          return typeOrder;
        }

        return a.id.localeCompare(b.id);
      }),
    [conditionForms]
  );

  return (
    <main>
      <TableQueryWrapper isLoading={isConditionFormsLoading} isError={isConditionFormsError}>
        {sortedConditionForms.length === 0 ? (
          <EmptyState
            subtitle={
              <Paragraph>
                <Trans id="settings.organization.data-catalogs.condition-forms.empty">
                  No condition forms yet.
                </Trans>
              </Paragraph>
            }
            illustration={<IllustrationNoResults />}
          />
        ) : (
          <Table
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.condition-forms.aria-label',
              message: 'Condition forms',
            })}
            columnWidths={['1fr', 'auto']}
            bordered
          >
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.organization.data-catalogs.condition-forms.table.header.type">
                    Type
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.data-catalogs.condition-forms.table.header.questions">
                    Number of questions
                  </Trans>
                </Column>
              </Row>
            </Header>

            <Body>
              {sortedConditionForms.map((conditionForm) => (
                <Row
                  key={conditionForm.id}
                  to={`/settings_beta/workflows/condition-forms/${conditionForm.id}`}
                >
                  <Cell isLink>
                    <Paragraph weight="medium">
                      <ConditionFormTypeLabel type={conditionForm.type} />
                    </Paragraph>
                  </Cell>
                  <Cell isLink>
                    <Paragraph>
                      <Plural
                        id="settings.organization.data-catalogs.condition-forms.questions-count"
                        value={conditionForm.questions.length}
                        one="# question"
                        other="# questions"
                      />
                    </Paragraph>
                  </Cell>
                </Row>
              ))}
            </Body>
          </Table>
        )}
      </TableQueryWrapper>
    </main>
  );
};

const ConditionFormTypeLabel = ({ type }: { type: ConditionFormFromGetMany['type'] }) => {
  switch (type) {
    case 'initial_condition':
      return (
        <Trans id="settings.organization.data-catalogs.condition-forms.type.initial-condition">
          Initial condition
        </Trans>
      );
    case 'quality_control':
      return (
        <Trans id="settings.organization.data-catalogs.condition-forms.type.quality-control">
          Quality control
        </Trans>
      );
  }
};
